import {useEffect, useMemo} from 'react'
import type {Thread, ThreadSubject} from '@github-ui/diff-lines/conversation-types'
import type {DiffEntry} from '@github-ui/diff-lines'
import {buildDiffSubject} from '../helpers/diff-helpers'
import {useMarkersActions} from '../stores/selectors'
import {useThreadPreviewPositioning} from '../page-data/loaders/use-thread-preview-positioning'

const POSITIONING_PAGE_SIZE = 40

/**
 * Custom hook that manages thread positioning data for the comments side panel.
 *
 * This hook categorizes threads into three mutually exclusive groups:
 * 1. Threads with subject but no diffLines: Attempts to build diffLines locally from diff entries
 *    (includes threads without positioning - they're attempted but will skip if they lack positioning data)
 * 2. Threads needing server data: Fetches positioning data from server for:
 *    - Threads without subject
 *    - Threads that cannot build locally (no diff entry context available or outdated)
 * 3. Threads with subject and diffLines: Already complete, no action needed
 *
 * Processing order: Group 1 attempts local building first. Threads that fail (no context)
 * fall back to Group 2 for server fetch. When updating from server, existing subjects are preserved and
 * only diffLines are replaced.
 *
 * @param threadPreviews - Array of threads to process
 */
export function useCompositeThreadPositioning(threadPreviews: Thread[], diffEntries: Record<string, DiffEntry> = {}) {
  const {updateThreads} = useMarkersActions()

  // Single pass: categorize threads and build local subjects
  const {threadsNeedingPositioning, locallyBuiltSubjects} = useMemo(() => {
    const needsPositioning: number[] = []
    const builtSubjects = new Map<string, ThreadSubject>()

    for (const thread of threadPreviews) {
      // Always fetch threads without subject
      if (!thread.subject) {
        needsPositioning.push(Number(thread.id))
        continue
      }

      // Skip threads that already have diffLines (Group 3)
      if (thread.subject.diffLines) continue

      // Threads with subject but no positioning are complete enough
      if (!thread.positioning) continue

      // Skip FILE type threads
      if (thread.subjectType === 'FILE') continue

      // Skip if missing positioning data needed for building
      if (thread.positioning.endLine === null || thread.positioning.endLine === undefined) continue

      // Try to build locally
      const {diffSubject, hasContext} = buildDiffSubject(thread, diffEntries)

      if (!hasContext || !diffSubject) {
        // No context, need to fetch from server
        needsPositioning.push(Number(thread.id))
      } else {
        // Has context, store for Group 1 processing
        builtSubjects.set(thread.id, diffSubject)
      }
    }

    return {
      threadsNeedingPositioning: needsPositioning.slice(0, POSITIONING_PAGE_SIZE),
      locallyBuiltSubjects: builtSubjects,
    }
  }, [threadPreviews, diffEntries])

  // Fetch positioning data for threads that need it
  const {data: positioningData} = useThreadPreviewPositioning(threadsNeedingPositioning)

  // Group 1: Update threads with locally built diffLines
  useEffect(() => {
    if (locallyBuiltSubjects.size === 0) return

    updateThreads(threads => {
      return threads.map(thread => {
        const diffSubject = locallyBuiltSubjects.get(thread.id)
        if (!diffSubject) return thread

        return {
          ...thread,
          subject: diffSubject,
        }
      })
    })
  }, [locallyBuiltSubjects, updateThreads])

  // Group 2 continued: Update threads with fetched positioning data from server
  useEffect(() => {
    if (!positioningData || Object.keys(positioningData).length === 0) return

    updateThreads(threads => {
      return threads.map(thread => {
        const threadId = Number(thread.id)
        const positioning = positioningData[threadId]
        if (!positioning) return thread

        // If thread already has a subject, preserve it and only update diffLines
        const subject = thread.subject ? {...thread.subject, diffLines: positioning.diffLines} : positioning

        return {
          ...thread,
          path: positioning.path,
          originalDiffPathUri: positioning.originalDiffPathUri,
          isOutdated: positioning.isOutdated ?? thread.isOutdated,
          subject,
          // If we are loading positioning data, that means we didn't have the data
          shouldNotRenderInDiffLines: true,
        }
      })
    })
  }, [positioningData, updateThreads])
}
